import React from 'react';
import swal from 'sweetalert';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { NavLink } from 'react-router-dom';
import {
  User,
  Building2,
  Camera,
  IdCard,
  Lock,
  Eye,
  EyeOff,
  Landmark,
  FileText,
  UploadCloud,
  CheckCircle2,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  X,
  Image as ImageIcon,
  Mail,
  Phone,
  Building,
} from 'lucide-react';
import '../../../../styles/components/edit-profile.scss';
import {
  getUser,
  sendOTP,
  sendProfileOTP,
  updateUser,
  verifyOTP,
  verifyOTPCreds,
} from '../../../../models/users';
import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';
import { getLoggedUser } from '../../../../state/user';
import i18n from '../../../../i18n';

const user = getLoggedUser();

const BANK_OPTIONS = [
  'Bank Muscat',
  'National Bank of Oman',
  'Bank Dhofar',
  'Sohar International',
  'Ahli Bank',
  'HSBC Oman',
];

/* ---------------------------------------------------------------------- */
/* Shared bits                                                            */
/* ---------------------------------------------------------------------- */

const FieldError = ({ message }) =>
  message ? (
    <div className="ep-error">
      <AlertTriangle size={13} />
      <span>{message}</span>
    </div>
  ) : null;

const formatFileSize = (size) =>
  size < 1048576 ? `${Math.round(size / 1024)} KB` : `${(size / 1048576).toFixed(1)} MB`;

/**
 * Drag/drop + click-to-browse upload field with an inline preview once a
 * file is selected (or already exists on the server), matching the
 * reference dropzone + preview-card pattern.
 */
class FileUploadZone extends React.Component {
  state = { isDragging: false };

  open = () => document.getElementById(this.props.inputId)?.click();

  render() {
    const {
      inputId,
      label,
      required,
      hint,
      accept = 'image/*,application/pdf',
      file,
      previewUrl,
      existingUrl,
      onSelect,
      onRemove,
      error,
    } = this.props;
    const { isDragging } = this.state;

    const displayUrl = previewUrl || existingUrl;
    const hasFile = !!file || !!existingUrl;
    const isPdf = file ? file.type === 'application/pdf' : /\.pdf($|\?)/i.test(existingUrl || '');

    return (
      <div className="ep-field">
        <label className="ep-field-label" htmlFor={inputId}>
          {label} {required && <span className="ep-required">*</span>}
        </label>

        <input
          id={inputId}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={(e) => onSelect(e.target.files?.[0])}
        />

        {!hasFile ? (
          <div
            className={`ep-dropzone${isDragging ? ' ep-dropzone--active' : ''}${
              error ? ' ep-dropzone--error' : ''
            }`}
            role="button"
            tabIndex={0}
            onClick={this.open}
            onKeyDown={(e) => (e.key === 'Enter' ? this.open() : null)}
            onDragOver={(e) => {
              e.preventDefault();
              this.setState({ isDragging: true });
            }}
            onDragLeave={() => this.setState({ isDragging: false })}
            onDrop={(e) => {
              e.preventDefault();
              this.setState({ isDragging: false });
              onSelect(e.dataTransfer.files?.[0]);
            }}
          >
            <span className="ep-dropzone__icon">
              <UploadCloud size={20} />
            </span>
            <p className="ep-dropzone__title">
              Drop file here or{' '}
              <span
                className="ep-dropzone__browse"
                onClick={(e) => {
                  e.stopPropagation();
                  this.open();
                }}
              >
                browse
              </span>
            </p>
            <p className="ep-dropzone__sub">Supports JPG, PNG, PDF — max 5MB</p>
          </div>
        ) : (
          <div className="ep-preview">
            <div className="ep-preview__media">
              {isPdf ? (
                <FileText size={26} />
              ) : displayUrl ? (
                <img src={displayUrl} alt={label} />
              ) : (
                <ImageIcon size={26} />
              )}
            </div>
            <div className="ep-preview__info">
              <span className="ep-preview__name">{file ? file.name : 'Uploaded file'}</span>
              <span className="ep-preview__size">
                {file ? formatFileSize(file.size) : 'On file'}
              </span>
            </div>
            <div className="ep-preview__actions">
              {displayUrl && (
                <a
                  href={displayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ep-preview__btn"
                >
                  View
                </a>
              )}
              <button type="button" className="ep-preview__btn" onClick={this.open}>
                Change
              </button>
              {file && (
                <button
                  type="button"
                  className="ep-preview__btn ep-preview__btn--danger"
                  onClick={onRemove}
                >
                  <X size={13} /> Remove
                </button>
              )}
            </div>
          </div>
        )}

        {hint && <p className="ep-field-hint">{hint}</p>}
        <FieldError message={error} />
      </div>
    );
  }
}

/**
 * Six-box OTP entry used by both verification modals. Auto-advances focus,
 * supports backspace-to-previous and paste-to-fill.
 */
class OtpBoxes extends React.Component {
  inputs = [];

  digits = () => {
    const { length = 6, value = '' } = this.props;
    return value.split('').concat(Array(length).fill('')).slice(0, length);
  };

  handleChange = (index, e) => {
    const { length = 6, onChange } = this.props;
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    const digits = this.digits();
    digits[index] = val;
    onChange(digits.join(''));
    if (val && index < length - 1) {
      this.inputs[index + 1]?.focus();
    }
  };

  handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !this.digits()[index] && index > 0) {
      this.inputs[index - 1]?.focus();
    }
  };

  handlePaste = (e) => {
    const { length = 6, onChange } = this.props;
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pasted) {
      onChange(pasted);
      e.preventDefault();
      const last = Math.min(pasted.length, length) - 1;
      this.inputs[last]?.focus();
    }
  };

  render() {
    const { length = 6 } = this.props;
    return (
      <div className="ep-otp-boxes" onPaste={this.handlePaste}>
        {this.digits().map((digit, i) => (
          <input
            key={i}
            ref={(el) => (this.inputs[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            className="ep-otp-box"
            value={digit}
            autoFocus={i === 0}
            onChange={(e) => this.handleChange(i, e)}
            onKeyDown={(e) => this.handleKeyDown(i, e)}
          />
        ))}
        {Array.from({ length }).length !== length && null}
      </div>
    );
  }
}

/* ---------------------------------------------------------------------- */
/* Main component                                                         */
/* ---------------------------------------------------------------------- */

export class EditProfileNew extends React.Component {
  constructor() {
    super();
    this.state = {
      user_loading: false,
      user: {},
      input: {
        is_company: false,
        password: '',
        confirm_password: '',
        otp: '',
      },
      verify: {},
      errors: {},
      files: {
        signature: null,
        file_id_number: null,
        file_auth_letter: null,
        file_cr_number: null,
        file_vat_certificate: null,
      },
      previews: {
        signature: null,
        file_id_number: null,
        file_auth_letter: null,
        file_cr_number: null,
        file_vat_certificate: null,
      },
      showPassword: false,
      showConfirmPassword: false,
      opt_modal: false,
      otp_loading: false,
      is_loading: false,
      is_mail_loading: false,
      is_phone_loading: false,
      otp_msg: '',
      open_popup: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleVerifyChange = this.handleVerifyChange.bind(this);
    this.handleBeforeSubmit = this.handleBeforeSubmit.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.sendProfileVerificationOTP = this.sendProfileVerificationOTP.bind(this);
    this.sendVerification = this.sendVerification.bind(this);
    this.verifyOTP = this.verifyOTP.bind(this);
  }

  async componentDidMount() {
    this.setState({ user_loading: true });
    window.scrollTo(0, 0);
    const response = await getUser();
    let userData = response.data;
    if (userData.mobile.length < 3) {
      userData.mobile = '';
    }
    this.setState({
      input: { ...this.state.input, ...userData },
      user: userData,
      user_loading: false,
    });
  }

  /* ---------------------------- validation ---------------------------- */

  validateFileType(file) {
    const fileName = file.name;
    const allowedExtensions = ['bmp', 'jpg', 'jpeg', 'png', 'gif'];
    const extension = fileName.substr(fileName.lastIndexOf('.') + 1).toLowerCase();
    return allowedExtensions.includes(extension);
  }

  validateFileSizeSig(file) {
    const MIN_FILE_SIZE = 5; // KB
    const MAX_FILE_SIZE = 5120; // KB (5MB)
    const fileSizeKiloBytes = file.size / 1024;
    return fileSizeKiloBytes >= MIN_FILE_SIZE && fileSizeKiloBytes <= MAX_FILE_SIZE;
  }

  validateFileSize(file) {
    const MIN_FILE_SIZE = 5; // KB
    const MAX_FILE_SIZE = 5120; // KB (5MB)
    const fileSizeKiloBytes = file.size / 1024;
    return fileSizeKiloBytes >= MIN_FILE_SIZE && fileSizeKiloBytes <= MAX_FILE_SIZE;
  }

  validateForm = () => {
    const { input } = this.state;
    const errors = {};

    if (!input.name?.trim()) errors.name = 'Full name is required';
    if (!input.username?.trim()) errors.username = 'Username is required';

    if (!input.email?.trim()) errors.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email))
      errors.email = 'Enter a valid email address';

    if (!input.mobile?.trim()) errors.mobile = 'Phone number is required';

    if (input.password) {
      if (input.password.length < 8) errors.password = 'Password must be at least 8 characters';
      if (input.confirm_password !== input.password)
        errors.confirm_password = 'Passwords do not match';
    }

    if (!input.resident_card_number?.trim()) errors.resident_card_number = 'ID number is required';

    if (!input.bank?.trim()) errors.bank = 'Bank name is required';
    if (!input.account_number?.trim()) errors.account_number = 'Account number is required';

    if (input.is_company) {
      if (!input.cr_number?.trim()) errors.cr_number = 'CR number is required';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  /* ------------------------------ files -------------------------------- */

  handleFileSelect = (name, file) => {
    if (!file) return;

    if (name === 'signature') {
      if (!this.validateFileSizeSig(file)) {
        this.setState((prev) => ({
          errors: { ...prev.errors, [name]: 'Allowed file size between 5KB - 5MB' },
        }));
        return;
      }
      if (!this.validateFileType(file)) {
        this.setState((prev) => ({
          errors: { ...prev.errors, [name]: 'Allowed only jpg/jpeg/png/gif/bmp images' },
        }));
        return;
      }
    } else if (!this.validateFileSize(file)) {
      this.setState((prev) => ({
        errors: { ...prev.errors, [name]: 'File size should be less than 5MB' },
      }));
      return;
    }

    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;

    this.setState((prev) => ({
      files: { ...prev.files, [name]: file },
      previews: { ...prev.previews, [name]: previewUrl },
      errors: { ...prev.errors, [name]: undefined },
    }));
  };

  removeFile = (name) => {
    this.setState((prev) => {
      if (prev.previews[name]) URL.revokeObjectURL(prev.previews[name]);
      return {
        files: { ...prev.files, [name]: null },
        previews: { ...prev.previews, [name]: null },
      };
    });
  };

  /* ------------------------------ inputs -------------------------------- */

  handleChange(event) {
    const { name, value } = event.target;
    this.setState((prev) => ({
      input: { ...prev.input, [name]: value },
      errors: { ...prev.errors, [name]: undefined },
    }));
  }

  handleVerifyChange(event) {
    const { name, value } = event.target;
    this.setState((prev) => ({ verify: { ...prev.verify, [name]: value } }));
  }

  /* ------------------------------- submit -------------------------------- */

  handleBeforeSubmit = async (event) => {
    this.setState({ is_loading: true });
    const response = await verifyOTP(this.state.verify);
    if (response.success) {
      this.handleSubmit(event);
    } else {
      this.setState({ is_loading: false });
      swal('Failed', 'Invalid OTP', 'error');
    }
  };

  handleSubmit = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    try {
      const { input, files } = this.state;
      const formData = new FormData();
      formData.append('name', input.name);
      formData.append('username', input.username);
      formData.append('email', input.email);
      formData.append('mobile', input.mobile);
      const password = input.password?.trim();

      if (password) {
        formData.append('password', password);
      }

      formData.append('resident_card_number', input.resident_card_number);
      formData.append('is_company', Boolean(input.is_company));
      formData.append('account_number', input.account_number);
      formData.append('bank', input.bank);
      formData.append('authority_name', input.authority_name);
      formData.append('vat_number', input.vat_number);
      formData.append('cr_number', input.cr_number);

      Object.entries(files).forEach(([key, file]) => {
        if (file) formData.append(key, file, file.name);
      });

      const response = await updateUser(formData);
      if ('id' in response.data) {
        swal(i18n.t('success'), response.message, 'success', {
          buttons: false,
          timer: 2000,
        })
          .then(() => {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('mzad_user_info', JSON.stringify(response.data));
            this.setState({
              is_loading: false,
              opt_modal: false,
              input: { ...this.state.input, ...response.data },
            });
          })
          .catch((res) => {
            swal('Failed', res.message, 'error');
          });
      } else {
        let error_message = response.message;
        const resObject = response.data;
        for (const key in resObject) {
          if (Object.hasOwnProperty.call(resObject, key)) {
            error_message += '\n' + resObject[key];
          }
        }
        swal('Failed', error_message, 'error');
        this.setState({ is_loading: false });
      }
    } catch (err) {
      console.log(err);
      this.setState({ is_loading: false });
    }
  };

  sendProfileVerificationOTP = async () => {
    if (!this.validateForm()) {
      toast.error('Please fix the highlighted fields before continuing');
      return;
    }
    const { password } = this.state.input;
    if (password && password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    this.setState({ otp_loading: true });
    const response = await sendProfileOTP();
    if (response.success) {
      this.setState({ opt_modal: true, otp_loading: false });
    } else {
      this.setState({ otp_loading: false });
      toast.error(response.message || 'Unable to send OTP');
    }
  };

  async sendVerification(type) {
    if (type === 'email') this.setState({ is_mail_loading: true });
    if (type === 'sms') this.setState({ is_phone_loading: true });

    const response = await sendOTP(type);
    const msg = response.message;
    this.setState({ otp_msg: msg });

    if (response.success) {
      this.setState((prev) => ({
        is_mail_loading: false,
        is_phone_loading: false,
        open_popup: true,
        input: { ...prev.input, verify_type: type },
      }));
    } else {
      swal('Failed', msg, 'error');
      this.setState({ is_mail_loading: false, is_phone_loading: false });
    }
  }

  async verifyOTP() {
    const { input } = this.state;
    const response = await verifyOTPCreds({ otp: input.otp, type: input.verify_type });
    if (response.success) {
      const userData = response.data;
      this.setState((prev) => ({
        user: userData,
        input: { ...prev.input, otp: '' },
        open_popup: false,
      }));
      swal(i18n.t('success'), response.message, 'success');
    } else {
      swal('Failed', response.message, 'error');
    }
  }

  /* ------------------------------- render -------------------------------- */

  renderSectionHeader(icon, title, subtitle) {
    return (
      <div className="ep-card__head">
        <span className="ep-card__icon">{icon}</span>
        <div>
          <h3 className="ep-card__title">{title}</h3>
          {subtitle && <p className="ep-card__sub">{subtitle}</p>}
        </div>
      </div>
    );
  }

  renderTypeToggle() {
    const { input } = this.state;
    return (
      <div className="ep-type-toggle">
        <button
          type="button"
          className={`ep-type-toggle__btn${!input.is_company ? ' ep-type-toggle__btn--active' : ''}`}
          onClick={() => this.setState((prev) => ({ input: { ...prev.input, is_company: false } }))}
        >
          <User size={15} /> {i18n.t('individual') || 'Individual'}
        </button>
        <button
          type="button"
          className={`ep-type-toggle__btn${input.is_company ? ' ep-type-toggle__btn--active' : ''}`}
          onClick={() => this.setState((prev) => ({ input: { ...prev.input, is_company: true } }))}
        >
          <Building2 size={15} /> {i18n.t('company') || 'Company'}
        </button>
      </div>
    );
  }

  renderFormContent() {
    const { input, errors, files, previews, showPassword, showConfirmPassword, otp_loading } =
      this.state;

    return (
      <div className="ep-form">
        {/* Profile Photo */}
        <section className="ep-card">
          {this.renderSectionHeader(
            <Camera size={17} />,
            'Profile Photo',
            'Shown on your bidder profile'
          )}
          <div className="ep-avatar-row">
            <div className="ep-avatar">
              {previews.profile_photo || input.avatar ? (
                <img src={previews.profile_photo || input.avatar} alt="Profile" />
              ) : (
                <User size={30} />
              )}
              <button
                type="button"
                className="ep-avatar__camera"
                onClick={() => document.getElementById('ep-upload-profile_photo').click()}
              >
                <Camera size={13} />
              </button>
            </div>
            <div>
              <input
                id="ep-upload-profile_photo"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => this.handleFileSelect('profile_photo', e.target.files?.[0])}
              />
              <button
                type="button"
                className="ep-btn ep-btn--outline"
                onClick={() => document.getElementById('ep-upload-profile_photo').click()}
              >
                Upload Photo
              </button>
              <p className="ep-field-hint">JPG or PNG, max 2MB</p>
            </div>
          </div>
        </section>

        {/* Basic Information */}
        <section className="ep-card">
          {this.renderSectionHeader(
            <IdCard size={17} />,
            'Basic Information',
            'Your personal details'
          )}
          <div className="ep-grid ep-grid--2">
            <div className="ep-field">
              <label className="ep-field-label">
                Full Name <span className="ep-required">*</span>
              </label>
              <input
                type="text"
                name="name"
                className={`ep-input${errors.name ? ' ep-input--error' : ''}`}
                value={input.name || ''}
                onChange={this.handleChange}
              />
              <FieldError message={errors.name} />
            </div>
            <div className="ep-field">
              <label className="ep-field-label">Username</label>
              <input
                type="text"
                name="username"
                className={`ep-input${errors.username ? ' ep-input--error' : ''}`}
                value={input.username || ''}
                onChange={this.handleChange}
              />
              <p className="ep-field-hint">Used for login and public profile</p>
              <FieldError message={errors.username} />
            </div>
          </div>
        </section>

        {/* Verification */}
        <section className="ep-card">
          {this.renderSectionHeader(
            <ShieldCheck size={17} />,
            'Verification',
            'Verify your contact details to secure your account'
          )}
          <div className="ep-grid ep-grid--2">
            <div className="ep-field">
              <label className="ep-field-label">
                Email Address <span className="ep-required">*</span>
              </label>
              <div className="ep-input-with-badge">
                <Mail size={15} className="ep-input-icon" />
                <input
                  type="email"
                  name="email"
                  className={`ep-input ep-input--icon${errors.email ? ' ep-input--error' : ''}`}
                  value={input.email || ''}
                  onChange={this.handleChange}
                />
                {this.state.user.email_verified_at ? (
                  <span className="ep-badge ep-badge--success">
                    <CheckCircle2 size={13} /> Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    className="ep-badge ep-badge--action"
                    onClick={() => this.sendVerification('email')}
                    disabled={this.state.is_mail_loading}
                  >
                    {this.state.is_mail_loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <ShieldAlert size={13} /> Verify Now
                      </>
                    )}
                  </button>
                )}
              </div>
              <FieldError message={errors.email} />
            </div>

            <div className="ep-field">
              <label className="ep-field-label">Phone Number</label>
              <div className="ep-input-with-badge">
                <Phone size={15} className="ep-input-icon" />
                <input
                  type="text"
                  name="mobile"
                  className={`ep-input ep-input--icon${errors.mobile ? ' ep-input--error' : ''}`}
                  value={input.mobile || ''}
                  onChange={this.handleChange}
                />
                {this.state.user.mobile_verified_at ? (
                  <span className="ep-badge ep-badge--success">
                    <CheckCircle2 size={13} /> Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    className="ep-badge ep-badge--action"
                    onClick={() => this.sendVerification('sms')}
                    disabled={this.state.is_phone_loading}
                  >
                    {this.state.is_phone_loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <ShieldAlert size={13} /> Verify Now
                      </>
                    )}
                  </button>
                )}
              </div>
              <FieldError message={errors.mobile} />
            </div>
          </div>
        </section>

        {/* Change Password */}
        <section className="ep-card">
          {this.renderSectionHeader(
            <Lock size={17} />,
            'Change Password',
            'Leave blank to keep your current password'
          )}
          <div className="ep-grid ep-grid--2">
            <div className="ep-field">
              <label className="ep-field-label">New Password</label>
              <div className="ep-input-with-icon">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className={`ep-input${errors.password ? ' ep-input--error' : ''}`}
                  placeholder="Enter new password"
                  value={input.password || ''}
                  onChange={this.handleChange}
                />
                <button
                  type="button"
                  className="ep-input-icon__btn"
                  onClick={() => this.setState((prev) => ({ showPassword: !prev.showPassword }))}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="ep-field-hint">Min 8 characters</p>
              <FieldError message={errors.password} />
            </div>
            <div className="ep-field">
              <label className="ep-field-label">Confirm Password</label>
              <div className="ep-input-with-icon">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirm_password"
                  className={`ep-input${errors.confirm_password ? ' ep-input--error' : ''}`}
                  placeholder="Repeat new password"
                  value={input.confirm_password || ''}
                  onChange={this.handleChange}
                />
                <button
                  type="button"
                  className="ep-input-icon__btn"
                  onClick={() =>
                    this.setState((prev) => ({ showConfirmPassword: !prev.showConfirmPassword }))
                  }
                >
                  {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <FieldError message={errors.confirm_password} />
            </div>
          </div>
        </section>

        {/* Residential ID */}
        <section className="ep-card">
          {this.renderSectionHeader(
            <IdCard size={17} />,
            'Residential ID',
            'Your national identification details'
          )}
          <div className="ep-field">
            <label className="ep-field-label">
              ID Number <span className="ep-required">*</span>
            </label>
            <input
              type="text"
              name="resident_card_number"
              className={`ep-input${errors.resident_card_number ? ' ep-input--error' : ''}`}
              value={input.resident_card_number || ''}
              onChange={this.handleChange}
            />
            <FieldError message={errors.resident_card_number} />
          </div>
          <FileUploadZone
            inputId="ep-upload-file_id_number"
            label="ID Attachment"
            hint="Upload a clear photo or scan of your ID"
            file={files.file_id_number}
            previewUrl={previews.file_id_number}
            existingUrl={input.file_id_number}
            error={errors.file_id_number}
            onSelect={(file) => this.handleFileSelect('file_id_number', file)}
            onRemove={() => this.removeFile('file_id_number')}
          />
        </section>

        {/* Signature */}
        <section className="ep-card">
          {this.renderSectionHeader(
            <FileText size={17} />,
            'Signature',
            'Used on official auction documents'
          )}
          <FileUploadZone
            inputId="ep-upload-signature"
            label="Signature"
            hint="Image dimension should be 200px (W) x 50px (H)"
            file={files.signature}
            previewUrl={previews.signature}
            existingUrl={input.signature}
            error={errors.signature}
            onSelect={(file) => this.handleFileSelect('signature', file)}
            onRemove={() => this.removeFile('signature')}
          />
        </section>

        {/* Banking Details */}
        <section className="ep-card">
          {this.renderSectionHeader(
            <Landmark size={17} />,
            'Banking Details',
            'Your payment and withdrawal account'
          )}
          <div className="ep-grid ep-grid--2">
            <div className="ep-field">
              <label className="ep-field-label">
                Bank Name <span className="ep-required">*</span>
              </label>
              <input
                list="ep-bank-options"
                type="text"
                name="bank"
                className={`ep-input${errors.bank ? ' ep-input--error' : ''}`}
                value={input.bank || ''}
                onChange={this.handleChange}
              />
              <datalist id="ep-bank-options">
                {BANK_OPTIONS.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
              <FieldError message={errors.bank} />
            </div>
            <div className="ep-field">
              <label className="ep-field-label">
                Account Number <span className="ep-required">*</span>
              </label>
              <input
                type="text"
                name="account_number"
                className={`ep-input${errors.account_number ? ' ep-input--error' : ''}`}
                value={input.account_number || ''}
                onChange={this.handleChange}
              />
              <FieldError message={errors.account_number} />
            </div>
          </div>
          <div className="ep-banner ep-banner--warning">
            <AlertTriangle size={15} />
            <span>
              Banking details are used for enrollment fee refunds and auction settlements. Changes
              require re-verification.
            </span>
          </div>
        </section>

        {/* Company Registration */}
        {(input.is_company || input.is_company !== 0) && (
          <section className="ep-card">
            {this.renderSectionHeader(
              <Building size={17} />,
              'Company Registration',
              'Required for company accounts'
            )}
            <div className="ep-grid ep-grid--2">
              <div className="ep-field">
                <label className="ep-field-label">
                  CR Number <span className="ep-required">*</span>
                </label>
                <input
                  type="text"
                  name="cr_number"
                  className={`ep-input${errors.cr_number ? ' ep-input--error' : ''}`}
                  value={input.cr_number || ''}
                  onChange={this.handleChange}
                />
                <FieldError message={errors.cr_number} />
              </div>
              <div className="ep-field">
                <label className="ep-field-label">VAT Number</label>
                <input
                  type="text"
                  name="vat_number"
                  className="ep-input"
                  value={input.vat_number || ''}
                  onChange={this.handleChange}
                />
              </div>
              <div className="ep-field">
                <label className="ep-field-label">Authorized Person Name</label>
                <input
                  type="text"
                  name="authority_name"
                  className="ep-input"
                  value={input.authority_name || ''}
                  onChange={this.handleChange}
                />
              </div>
            </div>

            <FileUploadZone
              inputId="ep-upload-file_cr_number"
              label="CR Certificate Attachment"
              hint="Upload your company registration certificate"
              file={files.file_cr_number}
              previewUrl={previews.file_cr_number}
              existingUrl={input.file_cr_number}
              error={errors.file_cr_number}
              onSelect={(file) => this.handleFileSelect('file_cr_number', file)}
              onRemove={() => this.removeFile('file_cr_number')}
            />
            <FileUploadZone
              inputId="ep-upload-file_vat_certificate"
              label="VAT Certificate Attachment"
              hint="Upload your VAT registration certificate"
              file={files.file_vat_certificate}
              previewUrl={previews.file_vat_certificate}
              existingUrl={input.file_vat_certificate}
              error={errors.file_vat_certificate}
              onSelect={(file) => this.handleFileSelect('file_vat_certificate', file)}
              onRemove={() => this.removeFile('file_vat_certificate')}
            />
            <FileUploadZone
              inputId="ep-upload-file_auth_letter"
              label="Authorization Letter"
              hint="Upload a signed letter authorizing this account"
              file={files.file_auth_letter}
              previewUrl={previews.file_auth_letter}
              existingUrl={input.file_auth_letter}
              error={errors.file_auth_letter}
              onSelect={(file) => this.handleFileSelect('file_auth_letter', file)}
              onRemove={() => this.removeFile('file_auth_letter')}
            />
          </section>
        )}

        <div className="ep-form__footer">
          <button
            type="button"
            className="ep-btn ep-btn--primary"
            onClick={this.sendProfileVerificationOTP}
            disabled={otp_loading}
          >
            {otp_loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <CheckCircle2 size={16} /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  renderOtpModal() {
    const { opt_modal, verify, is_loading } = this.state;
    return (
      <Modal
        show={opt_modal}
        onHide={() => this.setState({ opt_modal: false })}
        backdrop="static"
        centered
        dialogClassName="ep-otp-modal"
      >
        <Modal.Body className="ep-otp-modal__body">
          <button
            type="button"
            className="ep-otp-modal__close"
            onClick={() => this.setState({ opt_modal: false })}
          >
            <X size={16} />
          </button>
          <div className="ep-otp-modal__icon">
            <ShieldCheck size={22} />
          </div>
          <h3 className="ep-otp-modal__title">Confirm it's you</h3>
          <p className="ep-otp-modal__sub">
            {i18n.t('otp_verification_msg') ||
              'Enter the verification code we sent to confirm these profile changes.'}
          </p>

          <OtpBoxes
            value={verify.otp || ''}
            onChange={(val) => this.setState((prev) => ({ verify: { ...prev.verify, otp: val } }))}
          />

          <button
            type="button"
            className="ep-btn ep-btn--primary ep-btn--block"
            onClick={this.handleBeforeSubmit}
            disabled={is_loading || (verify.otp || '').length < 6}
          >
            {is_loading ? <Spinner animation="border" size="sm" /> : 'Verify & Save'}
          </button>
        </Modal.Body>
      </Modal>
    );
  }

  renderPhoneEmailOtpModal() {
    const { open_popup, otp_msg, input } = this.state;
    return (
      <Modal
        show={open_popup}
        onHide={() => this.setState({ open_popup: false })}
        backdrop="static"
        centered
        dialogClassName="ep-otp-modal"
      >
        <Modal.Body className="ep-otp-modal__body">
          <button
            type="button"
            className="ep-otp-modal__close"
            onClick={() => this.setState({ open_popup: false })}
          >
            <X size={16} />
          </button>
          <div className="ep-otp-modal__icon">
            <ShieldCheck size={22} />
          </div>
          <h3 className="ep-otp-modal__title">
            Verify {input.verify_type === 'email' ? 'email' : 'phone'}
          </h3>
          <p className="ep-otp-modal__sub">{otp_msg || 'Enter the code we just sent you.'}</p>

          <OtpBoxes
            value={input.otp || ''}
            onChange={(val) => this.setState((prev) => ({ input: { ...prev.input, otp: val } }))}
          />

          <button
            type="button"
            className="ep-btn ep-btn--primary ep-btn--block"
            onClick={this.verifyOTP}
            disabled={(input.otp || '').length < 6}
          >
            Verify
          </button>
        </Modal.Body>
      </Modal>
    );
  }

  render() {
    const isClientRole = user?.role === 'C';
    const modals = (
      <>
        {this.renderOtpModal()}
        {this.renderPhoneEmailOtpModal()}
      </>
    );

    if (isClientRole) {
      return (
        <React.Fragment>
          <div className="cd-page">
            <div className="cd-shell">
              <p className="cd-crumb">
                <NavLink to="/client-management">{i18n.t('dashboard')}</NavLink>
                <span className="cd-crumb__current">
                  {i18n.t('edit_profile') || 'Edit Profile'}
                </span>
              </p>

              <header className="cd-hero cd-hero--compact ep-hero">
                <div>
                  <h1 className="cd-hero__title">{i18n.t('edit_profile') || 'Edit Profile'}</h1>
                  <p className="cd-hero__sub">Manage your personal and verification details</p>
                </div>
                {this.renderTypeToggle()}
              </header>

              {this.renderFormContent()}
            </div>
          </div>
          {modals}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <HeaderBanner title={i18n.t('edit_profile')} />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                <SideBar />
              </div>
              <div className="col-xl-9 col-lg-12 col-md-12">
                <div className="ep-hero ep-hero--internal">
                  <div>
                    <h1 className="ep-hero__title">{i18n.t('edit_profile')}</h1>
                    <p className="ep-hero__sub">Manage your personal and verification details</p>
                  </div>
                  {this.renderTypeToggle()}
                </div>
                {this.renderFormContent()}
              </div>
            </div>
          </div>
        </section>
        {modals}
      </React.Fragment>
    );
  }
}
